import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { bookingsAPI } from '../../../services/api';
import { CalendarRange, Search, Filter, Plus, ChevronRight, Clock, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const BookingList = () => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        fetchBookings();
    }, [filterStatus]);

    const fetchBookings = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterStatus !== 'all') params.status = filterStatus;

            const response = await bookingsAPI.getAll(params);
            setBookings(response.bookings || []);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            toast.error('Failed to load bookings');
        } finally {
            setLoading(false);
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'pending': return isDarkMode ? 'bg-yellow-900/40 text-yellow-500 border-yellow-800' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'confirmed': return isDarkMode ? 'bg-blue-900/40 text-blue-400 border-blue-800' : 'bg-blue-100 text-blue-800 border-blue-200';
            case 'converted': return isDarkMode ? 'bg-green-900/40 text-green-400 border-green-800' : 'bg-green-100 text-green-800 border-green-200';
            case 'expired':
            case 'cancelled': return isDarkMode ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-red-100 text-red-800 border-red-200';
            default: return isDarkMode ? 'bg-zinc-800 text-zinc-400 border-zinc-700' : 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <Clock size={14} className="mr-1" />;
            case 'confirmed': return <CheckCircle size={14} className="mr-1" />;
            case 'converted': return <CheckCircle size={14} className="mr-1" />;
            case 'cancelled':
            case 'expired': return <XCircle size={14} className="mr-1" />;
            default: return null;
        }
    };

    const filteredBookings = bookings.filter(b =>
    (b.prospectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        b.prospectPhone?.includes(searchTerm) ||
        b.apartmentNumber?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {t('bookings')}
                    </h1>
                    <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {t('monthly_view_holds')}
                    </p>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Link to="/admin/bookings/calendar" className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center transition-colors ${isDarkMode ? 'border-zinc-700 bg-zinc-800 text-white hover:bg-zinc-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {t('calendar_view')}
                    </Link>
                    <Link to="/admin/bookings/waitlist" className={`px-4 py-2 rounded-lg border text-sm font-medium flex items-center transition-colors ${isDarkMode ? 'border-zinc-700 bg-zinc-800 text-white hover:bg-zinc-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}>
                        {t('waitlist')}
                    </Link>
                    <Link to="/admin/bookings/add" className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center transition-colors">
                        <Plus size={18} className="mr-2" />
                        {t('new_booking')}
                    </Link>
                </div>
            </div>

            <div className={`p-4 rounded-xl border flex flex-col md:flex-row gap-4 justify-between ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                <div className="relative w-full md:w-96 flex-shrink-0">
                    <Search className={`absolute start-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`} size={18} />
                    <input
                        type="text"
                        placeholder={t('search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={`w-full ps-10 pe-4 py-2 rounded-lg border text-sm focus:ring-2 focus:ring-red-500 outline-none transition-colors ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500' : 'bg-gray-50 border-gray-300 text-gray-900'}`}
                    />
                </div>

                <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0">
                    {['all', 'pending', 'confirmed', 'converted', 'expired'].map(status => (
                        <button
                            key={status}
                            onClick={() => setFilterStatus(status)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${filterStatus === status
                                ? (isDarkMode ? 'bg-red-900/30 text-red-500 border-red-800' : 'bg-red-50 text-red-700 border-red-200')
                                : (isDarkMode ? 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:bg-zinc-700' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')
                                }`}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
            ) : filteredBookings.length === 0 ? (
                <div className={`text-center py-16 rounded-xl border ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'}`}>
                    <CalendarRange size={48} className={`mx-auto mb-4 ${isDarkMode ? 'text-zinc-700' : 'text-gray-300'}`} />
                    <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('no_bookings_found')}</h3>
                    <p className={`mt-1 mb-6 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                        {searchTerm || filterStatus !== 'all' ? t('try_again') : t('new_booking_hold')}
                    </p>
                    <Link to="/admin/bookings/add" className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors">
                        <Plus size={18} className="mr-2" />
                        {t('create_booking')}
                    </Link>
                </div>
            ) : (
                <div className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-gray-200 bg-white'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full text-start text-sm">
                            <thead className={`text-xs uppercase ${isDarkMode ? 'bg-zinc-800/50 text-zinc-400' : 'bg-gray-50 text-gray-500'}`}>
                                <tr>
                                    <th className="px-6 py-4 font-medium">{t('prospect')}</th>
                                    <th className="px-6 py-4 font-medium">{t('apartment')}</th>
                                    <th className="px-6 py-4 font-medium">{t('requested_dates')}</th>
                                    <th className="px-6 py-4 font-medium">{t('status')}</th>
                                    <th className="px-6 py-4 font-medium text-end">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${isDarkMode ? 'divide-zinc-800' : 'divide-gray-100'}`}>
                                {filteredBookings.map((booking) => (
                                    <tr key={booking.id} className={`group transition-colors ${isDarkMode ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-50'}`}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {booking.prospectName}
                                            </div>
                                            <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                                {booking.prospectPhone}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={`font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>
                                                {t('apartment_display', { number: booking.apartmentNumber })}
                                            </div>
                                            <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                                                {booking.buildingName} • {t('floor_display', { number: booking.floor })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className={isDarkMode ? 'text-zinc-300' : 'text-gray-700'}>
                                                {new Date(booking.moveInDate).toLocaleDateString()}
                                            </div>
                                            <div className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>
                                                {t('to')}: {booking.moveOutDate ? new Date(booking.moveOutDate).toLocaleDateString() : t('none')}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(booking.status)}`}>
                                                {getStatusIcon(booking.status)}
                                                <span className="capitalize">{booking.status}</span>
                                            </span>
                                            {booking.status === 'pending' && booking.expiresAt && (
                                                <div className={`text-[10px] mt-1.5 ${isDarkMode ? 'text-yellow-600' : 'text-yellow-600'}`}>
                                                    {t('hold_expires')}: {new Date(booking.expiresAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-end">
                                            <button
                                                onClick={() => navigate(`/admin/bookings/${booking.id}`)}
                                                className={`p-2 rounded-lg inline-flex items-center transition-colors ${isDarkMode ? 'text-zinc-400 hover:bg-zinc-700 hover:text-white' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-900'}`}
                                            >
                                                <ChevronRight size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingList;
